// To book (office-booking prototype): customers asking to move or cancel a visit (F-08), estimates the
// customer signed "for later", membership visits that are due, and leads not booked yet. Each needs
// someone here; nothing books or moves itself.

import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { DAY_WINDOWS, dayOptions, today as todayIn, windowLabel, zonedInstant } from '../book-model.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { openJob } from '../job/job-page.ts';
import { EstimateDeposits } from '../job/Deposits.tsx';
import { moneyRights } from '../history/money-model.ts';
import { DISPATCH_PERMS } from '../job/job-model.ts';
import { money } from '../review/review-model.ts';
import { LeadsSection } from './Leads.tsx';
import { LeadSearch } from './LeadSearch.tsx';
import { VisitRequestsSection } from './VisitRequests.tsx';
import {
  dueHorizon,
  dueWords,
  soldState,
  type DueVisit,
  type Lead,
  type SoldEstimate,
} from './tobook-model.ts';
import { Loading } from '../shared/Loading.tsx';
import '../review/review.css';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import './tobook.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';
const longDate = (d: string) =>
  new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${d}T12:00:00Z`));

export function ToBookScreen({
  nav,
  timeZone,
  onBook,
  onBookLead,
  onChanged,
}: {
  nav: NavProps;
  timeZone: string;
  onBook: (estimate: SoldEstimate) => void;
  onBookLead: (lead: Lead) => void;
  /** Something here changed what waits (a lead added or closed): the Inbox count re-reads. */
  onChanged: () => void;
}) {
  const today = todayIn(timeZone);
  const [sold, setSold] = useState<SoldEstimate[] | null>(null);
  const [due, setDue] = useState<DueVisit[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [picking, setPicking] = useState<DueVisit | null>(null);
  // A held or cancel-asked estimate being cleared, once someone has spoken with the customer (QA-576)
  const [clearing, setClearing] = useState<SoldEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  // While the company mirrors ServiceTitan, its sold work and membership visits are booked there, and
  // holds cleared there: listed here to see (BL-088). Leads stay ours.
  const mirroring = useMirror();
  useEffect(() => {
    client
      .call<{ estimates: SoldEstimate[] }>('estimates.soldNotBooked', {})
      .then((r) => setSold(r.estimates))
      .catch((e: unknown) => setError(message(e)));
    client
      .call<{ due: DueVisit[] }>('recurringServices.due', { asOf: dueHorizon(today) })
      .then((r) => setDue(r.due))
      .catch((e: unknown) => setError(message(e)));
  }, [today, tick]);

  // the four lists, one after another (Blueprint's To schedule is ToSchedulePage.tsx)
  const asked = (
    <VisitRequestsSection
      timeZone={timeZone}
      canMove={DISPATCH_PERMS.some((p) => (nav.permissions ?? []).includes(p))}
      onChanged={onChanged}
    />
  );
  const soldList = (
    <section className="sect" aria-label="Sold, not booked">
      <h2>Sold, not booked{sold ? ` (${sold.length})` : ''}</h2>
      {mirroring && <p className="muted small">{MIRROR_LINE}</p>}
      {sold === null ? (
        <Loading error={null} what="the sold estimates" />
      ) : sold.length ? (
        <div className="qgrid">
          {sold.map((e) => {
            const st = soldState(e);
            return (
              <article key={e.estimateId} className="stone qcard">
                <div className="row between">
                  <b>{e.customerName}</b>
                  <b>{money(e.totalCents)}</b>
                </div>
                <span className="muted small">
                  Estimate #{e.number} · {e.optionTitle} · from job #{e.jobNumber} ·{' '}
                  {new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(
                    new Date(e.soldAt),
                  )}
                </span>
                <span className={`chip say ${st.tone}`}>{st.chip}</span>
                {st.why && <span className="small">{st.why}</span>}
                <EstimateDeposits
                  estimateId={e.estimateId}
                  estimateNumber={e.number}
                  customerName={e.customerName}
                  totalCents={e.totalCents}
                  mayTake={moneyRights(nav.permissions ?? [], mirroring).record}
                  onTaken={setToast}
                />
                <>
                  <button type="button" className="btn quiet" onClick={() => openJob(e.jobId)}>
                    Open job #{e.jobNumber}
                  </button>
                  {st.clear && !mirroring && (
                    <button type="button" className="btn" onClick={() => setClearing(e)}>
                      {st.clear === 'hold' ? 'Clear the hold' : 'Clear their request'}
                    </button>
                  )}
                  {!mirroring && (
                    <button
                      className="btn primary"
                      disabled={!st.bookable}
                      onClick={() => onBook(e)}
                      aria-label={`Book estimate ${e.number} for ${e.customerName}`}
                    >
                      Book it
                    </button>
                  )}
                </>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="muted empty">Nothing waiting: every sold estimate is booked.</p>
      )}
    </section>
  );
  const dueList = (
    <section className="sect" aria-label="Membership visits due">
      <h2>Membership visits due in the next 30 days{due ? ` (${due.length} to book)` : ''}</h2>
      {due === null ? (
        <Loading error={null} what="the membership visits" />
      ) : due.length ? (
        <ul className="rows">
          {due.map((d) => {
            const w = dueWords(d.dueOn, today);
            return (
              <li key={d.recurringServiceId} className="stone mat">
                <div>
                  <b>{d.customerName}</b>
                  <p className="muted small">
                    {d.address} · {d.membershipName}: {d.jobTypeName} · {d.visitsLeft} visit
                    {d.visitsLeft === 1 ? '' : 's'} left · ends {longDate(d.endsOn)}
                  </p>
                </div>
                <span className={`chip say ${w.tone}`}>
                  {w.text} ({longDate(d.dueOn)})
                </span>
                {mirroring ? null : d.businessUnitId ? (
                  <button className="btn primary" onClick={() => setPicking(d)}>
                    Book visit
                  </button>
                ) : (
                  <span className="small">
                    The {d.jobTypeName} job type has no department yet, so this can’t be booked until it has
                    one.
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted empty">No membership visits due.</p>
      )}
    </section>
  );
  const leads = (
    <>
      <LeadsSection timeZone={timeZone} onBook={onBookLead} onChanged={onChanged} />
      {/* any lead, found by its name, phone, email or words, with its ad click (BL-092) */}
      <LeadSearch timeZone={timeZone} />
    </>
  );
  return (
    <div className="app">
      <Header
        nav={nav}
        title="To book"
        lede="Work waiting for someone here: customers asking to move a visit, sold estimates, membership visits due, and leads."
      />
      <main className="page review tobook">
        {error && <p className="error">{error}</p>}
        {toast && (
          <p className="notice ok" role="status">
            {toast}
          </p>
        )}
        <>
          {asked}
          {soldList}
          {dueList}
          {leads}
        </>
      </main>
      {clearing && (
        <ClearDialog
          estimate={clearing}
          onClose={() => setClearing(null)}
          onCleared={() => {
            setToast(`Cleared. ${clearing.customerName}’s estimate #${clearing.number} can be booked now.`);
            setClearing(null);
            setTick((t) => t + 1);
          }}
        />
      )}
      {picking && (
        <VisitDialog
          visit={picking}
          today={today}
          timeZone={timeZone}
          onClose={() => setPicking(null)}
          onBooked={(n) => {
            setToast(`Booked job ${n} for ${picking.customerName}. Assign the technician on the board.`);
            setPicking(null);
            setTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}

function VisitDialog({
  visit,
  today,
  timeZone,
  onClose,
  onBooked,
}: {
  visit: DueVisit;
  today: string;
  timeZone: string;
  onClose: () => void;
  onBooked: (jobNumber: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const days = dayOptions(today, 14);
  const [date, setDate] = useState(days[1]!.date);
  const [win, setWin] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The same day and window, booked again after a lost answer, keep the key: never booked twice.
  const save = useSaveKey(`${visit.recurringServiceId}:${date}:${win}`);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const book = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const [s, e] = DAY_WINDOWS[win]!;
      const r = await client.call<{ jobNumber: string }>(
        'memberships.bookVisit',
        {
          recurringServiceId: visit.recurringServiceId,
          businessUnitId: visit.businessUnitId!,
          start: zonedInstant(date, s, timeZone),
          end: zonedInstant(date, e, timeZone),
        },
        { idempotencyKey: save.key },
      );
      onBooked(r.jobNumber);
    } catch (e) {
      const lost = !(e instanceof RpcError) || e.code === 'offline' || e.code === 'internal';
      setError(lost ? 'Couldn’t book the visit. Try again; it won’t be booked twice.' : message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="visit-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void book();
        }}
      >
        <h3 id="visit-title">Book {visit.customerName}’s visit</h3>
        <p className="muted">
          {visit.membershipName}: {visit.jobTypeName} at {visit.address}. Assign the technician on the board.
        </p>
        <label>
          Day
          <select className="field" value={date} onChange={(e) => setDate(e.target.value)}>
            {days.map((d) => (
              <option key={d.date} value={d.date}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Arrival window
          <select className="field" value={win} onChange={(e) => setWin(Number(e.target.value))}>
            {DAY_WINDOWS.map((w, i) => (
              <option key={i} value={i}>
                {windowLabel(w)}
              </option>
            ))}
          </select>
        </label>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton busy={busy} busyLabel="Booking…" error={error} onClick={() => void book()}>
            Book visit
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}

/** Clear a held or cancel-asked estimate: what the customer said is kept with it (estimates.clearHold). */
function ClearDialog({
  estimate,
  onClose,
  onCleared,
}: {
  estimate: SoldEstimate;
  onClose: () => void;
  onCleared: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const hold = soldState(estimate).clear === 'hold';
  const [said, setSaid] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const clear = async () => {
    if (busy) return;
    if (!said.trim()) {
      setError('Say what the customer told you.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await client.call('estimates.clearHold', {
        estimateId: estimate.estimateId,
        clear: hold ? 'hold' : 'cancel_request',
        said: said.trim(),
      });
      onCleared();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="clear-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void clear();
        }}
      >
        <h3 id="clear-title">{hold ? 'Clear the hold' : 'Clear their request to cancel'}</h3>
        <p className="muted">
          {hold
            ? `${estimate.customerName} said the signature on estimate #${estimate.number} was wrong. Clear it only once you’ve spoken with them and they want the work. If they want it cancelled, cancel the estimate instead.`
            : `${estimate.customerName} asked to cancel estimate #${estimate.number}. Clear it only if, when you called, they still want the work. If they still want it cancelled, cancel the estimate instead.`}
        </p>
        <label>
          What they told you
          <textarea
            className="field"
            rows={3}
            maxLength={500}
            value={said}
            onChange={(e) => setSaid(e.target.value)}
          />
        </label>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton busy={busy} busyLabel="Clearing…" error={error} onClick={() => void clear()}>
            {hold ? 'Clear the hold' : 'Clear their request'}
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}
